import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { PermissionRoles } from "@/enum";
import { ROUTE_CONFIG } from "@/routes/config";
import { useHasPermissions } from "@/utils/useHasPermissions";

import { TopupCreditsModal } from "../TopupCreditsModal/TopupCreditsModal";

interface CreditTopUpProps {
	hasSubscription: boolean;
	/** Refetches the balance once the top-up dialog closes. */
	onClosed: () => void;
}

export const PLANS_AND_BILLING_PATH = `${ROUTE_CONFIG.COMPANY}?tab=BILLING`;

/**
 * "Top up" beside a balance that does not cover an action (PREC-4263). A user who may buy credits
 * (SUBSCRIPTION permission, a subscription to buy on) gets the existing top-up dialog, whose
 * checkout opens in a new tab so the selection here survives; anyone else gets a link to Plans &
 * Billing, in a new tab for the same reason. The dialog renders inside the open credit dialog, so
 * it stacks above it.
 */
export const CreditTopUp: FunctionComponent<CreditTopUpProps> = ({ hasSubscription, onClosed }) => {
	const { t } = useTranslation();
	const { hasPermissions } = useHasPermissions();
	const [showTopUp, setShowTopUp] = useState(false);
	const canBuy = hasSubscription && hasPermissions([PermissionRoles.SUBSCRIPTION]);

	if (!canBuy) {
		return (
			<Link
				className="font-medium text-brand-light underline cursor-pointer"
				data-test="credit-confirm-billing-link"
				rel="noreferrer"
				target="_blank"
				to={PLANS_AND_BILLING_PATH}
			>
				{t("creditConfirm.plansAndBilling")}
			</Link>
		);
	}

	return (
		<>
			<button
				className="font-medium text-brand-light underline cursor-pointer"
				data-test="credit-confirm-top-up"
				type="button"
				onClick={() => setShowTopUp(true)}
			>
				{t("creditConfirm.topUp")}
			</button>
			<TopupCreditsModal
				checkoutInNewTab
				setShow={(show) => {
					setShowTopUp(show);

					if (!show) {
						onClosed();
					}
				}}
				show={showTopUp}
			/>
		</>
	);
};
